import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { ExternalLink, ShieldAlert, Filter } from "lucide-react";

const SEV_COLORS = {
  CRITICAL: "#ff4d6d",
  HIGH: "#ff8a4d",
  MEDIUM: "#fbbf24",
  LOW: "#39ff14",
  UNKNOWN: "#94a3b8",
};

export default function CyberNews() {
  const [filter, setFilter] = useState("all");
  const [news, setNews] = useState([]);
  const [cves, setCves] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get(`/news/feed`, { params: { filter } }),
        api.get(`/news/cve-ticker`),
      ]);
      setNews(a.data.items || []);
      setCves(b.data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);
  useEffect(() => {
    const t = setInterval(() => { api.get(`/news/cve-ticker`).then((r) => setCves(r.data.items || [])).catch(()=>{}); }, 600_000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="space-y-5" data-testid="news-tab">
      <div className="csc-card overflow-hidden border-[#00aaff]/40" style={{ boxShadow: "0 0 18px rgba(0, 170, 255,0.18)" }} data-testid="cve-ticker">
        <div className="flex items-center gap-2 px-4 py-2 border-b border-[rgba(0,170,255,0.18)]">
          <ShieldAlert size={14} className="text-[#39ff14]" />
          <span className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8]">Live CVE Ticker · refreshed every 10 min</span>
        </div>
        <div className="relative overflow-hidden h-9">
          <div className="absolute inset-0 flex items-center whitespace-nowrap animate-[scroll_60s_linear_infinite]">
            {(cves.length ? cves.concat(cves) : []).map((c, i) => (
              <span key={`${c.id}-${i}`} className="px-6 font-mono-tech text-xs">
                <span style={{ color: SEV_COLORS[c.severity] || "#94a3b8" }} className="font-bold">{c.severity}</span>
                <span className="text-white ml-2">{c.id}</span>
                {c.score != null && <span className="text-[#94a3b8] ml-2">CVSS {c.score}</span>}
                <span className="text-[#94a3b8] ml-3">{c.summary?.slice(0, 120)}…</span>
              </span>
            ))}
          </div>
        </div>
        <style>{`@keyframes scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
      </div>

      <div className="flex items-center gap-2">
        <Filter size={14} className="text-[#94a3b8]" />
        <button onClick={() => setFilter("all")} className={`csc-pill ${filter === "all" ? "csc-pill-active" : ""}`} data-testid="news-filter-all">All News</button>
        <button onClick={() => setFilter("nonprofit")} className={`csc-pill ${filter === "nonprofit" ? "csc-pill-active" : ""}`} data-testid="news-filter-nonprofit">Nonprofit / Charity Focused</button>
      </div>

      {loading ? (
        <div className="font-mono-tech text-xs text-[#94a3b8] uppercase tracking-wider">Loading…</div>
      ) : news.length === 0 ? (
        <div className="csc-card p-8 text-center text-[#94a3b8]" data-testid="news-empty">No matching articles right now.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {news.map((n, i) => (
            <a key={i} href={n.link} target="_blank" rel="noopener noreferrer" className="csc-card p-4 hover:border-[#00aaff] block" data-testid={`news-card-${i}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#00aaff]">{n.source}</span>
                {n.nonprofit_relevant && (
                  <span className="font-mono-tech text-[9px] uppercase tracking-wider px-2 py-0.5 rounded bg-[#39ff14]/15 text-[#39ff14] border border-[#39ff14]/30">Nonprofit Relevant</span>
                )}
              </div>
              <h3 className="text-sm font-semibold text-white mt-1">{n.title}</h3>
              <p className="text-xs text-[#94a3b8] mt-2 line-clamp-3">{n.summary}</p>
              <div className="mt-3 flex items-center justify-between font-mono-tech text-[10px] text-[#94a3b8]">
                <span>{n.published}</span>
                <span className="text-[#00aaff] inline-flex items-center gap-1"><ExternalLink size={11} /> Read more</span>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
